'use client';

import cn from 'classnames';
import { useLocale, useTranslations } from 'next-intl';

import { Icon } from '@components/icons';
import { Link, usePathname } from '@lib/navigation';
import { DesktopCategory, getMenuItems, MenuItem } from '@utils/menu';

const getFilteredAndGroupedMenuItems = (locale: string, isPremiumEnabled: boolean) => {
  const menuItems = getMenuItems((item) => {
    if (item.version != 2) {
      return false;
    }
    if (!item.visibleInLang.includes(locale)) {
      return false;
    }

    return item.onDesktop && (item.label !== 'premium' || isPremiumEnabled);
  });
  const groups: Record<DesktopCategory, MenuItem[]> = menuItems.reduce(
    (acc, item) => {
      if (!acc[item.desktopCategory]) {
        acc[item.desktopCategory] = [];
      }
      acc[item.desktopCategory].push(item);
      return acc;
    },
    {} as Record<DesktopCategory, MenuItem[]>,
  );

  return groups;
};

const getLinkProps = (link: string) => {
  if (link.startsWith('http')) {
    return {
      href: link,
      target: '_blank',
    };
  }

  return {
    href: `/${link}`,
  };
};

interface HeaderNavbarProps {
  className?: string;
  isPremiumEnabled: boolean;
}

const ListParser = ({ list, className, t, pathname }: { className?: string; list: MenuItem[]; t: (arg: string) => string; pathname: string }) => {
  const baseItemClassName =
    'border border-opacity-10 bg-white border-premium-dark hover:opacity-30 flex h-full px-4 py-2 items-center justify-start rounded-full text-sm transition-colors whitespace-nowrap';

  if (!list) {
    return <></>;
  }

  const isActiveCategoryPath = (link: string) => {
    // Check if current path matches the menu item link
    const menuLink = t(link);
    if (menuLink.startsWith('category/')) {
      // Extract the category slug
      const categorySlug = menuLink.replace('category/', '');
      // Check if we're on this category page
      return pathname.startsWith(`/category/${categorySlug}`);
    }
    return false;
  };

  return (
    <ul className="flex items-center justify-start gap-3 p-2">
      {list.map((item) => {
        const { label, link, subMenu, classNames } = item;
        const isActive = isActiveCategoryPath(link);
        const activeClassName = isActive ? 'bg-premium-dark text-white border-premium-dark hover:bg-premium-900' : '';
        const isAdCategory = item.desktopCategory === 'ad';

        return (
          <li className={cn('group relative flex h-full flex-grow items-center justify-center', className)} key={link}>
            {!subMenu ? (
              <Link
                className={cn(
                  isAdCategory
                    ? 'flex h-full items-center justify-center px-2 py-2 text-sm font-bold transition-colors hover:opacity-70'
                    : baseItemClassName,
                  !isAdCategory && 'flex items-center justify-center gap-1 border shadow-md',
                  classNames,
                  !isAdCategory && (item.pill?.color && !isActive ? `bg-${item.pill.color}` : 'bg-white'),
                  !isAdCategory && (item.pill?.textColor && !isActive
                    ? `text-${item.pill.textColor} hover:text-${item.pill.textColor}`
                    : 'text-black hover:text-black'),
                  !isAdCategory && activeClassName,
                )}
                {...getLinkProps(t(link))}
              >
                {item.icon && <item.icon />}
                {t(label)}
              </Link>
            ) : (
              <>
                <span
                  className={cn(
                    'transition-border flex w-full w-full cursor-pointer items-center justify-center  text-center text-sm ',
                    classNames,
                    activeClassName,
                  )}
                >
                  {t(label)}
                  <Icon icon="chevron-right" className="rotate-90 bg-transparent" size={18} />
                </span>
                <ul className="pointer-events-none absolute left-0 top-full w-full list-none rounded-lg border border-premium-dark border-opacity-50  bg-white opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100">
                  {subMenu.map(({ label, link, classNames }) => {
                    const isSubActive = isActiveCategoryPath(link);
                    const subActiveClassName = isSubActive ? 'bg-premium-dark text-white' : '';
                    
                    return (
                    <li className="flex h-full items-center justify-center" key={link}>
                      <Link
                        className={cn('w-full p-2 text-center text-xs text-black hover:bg-gray-300', classNames, subActiveClassName)}
                        {...getLinkProps(t(link))}
                      >
                        {t(label)}
                      </Link>
                    </li>
                  )})}
                </ul>
              </>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default function HeaderNavbarV2({ className, isPremiumEnabled }: HeaderNavbarProps) {
  const locale = useLocale();
  const t = useTranslations('menu');
  const pathname = usePathname();

  const menuItems = getFilteredAndGroupedMenuItems(locale, isPremiumEnabled);

  return (
    <nav className={cn('absolute top-full w-full border-b border-t border-gray-500 px-2', className)}>
      <div className="absolute left-0 top-0 h-full w-full bg-white opacity-[0.8]  dark:bg-premium-dark" />
      <div className="container mx-auto">
        <ul className="flex items-center justify-start gap-3 p-2 lg:gap-1 lg:p-1">
          <ListParser list={menuItems.section} t={t} pathname={pathname} />
          <div className="mx-4 h-8 border-r border-premium-900 lg:mx-1" />
          <ListParser list={menuItems.media} t={t} pathname={pathname} />
          <div className="flex w-full flex-col items-end">
            <ListParser list={menuItems.ad} t={t} pathname={pathname} className="min-w-[200px] lg:min-w-[90px]" />
          </div>
        </ul>
      </div>
    </nav>
  );
}
